<template>
  <div class="scout">
    <ScoutHero :hero="hero" has-panel :hero-container-styles="{ backgroundColor: '#DBF0FA' }" />
    <ScoutCustomers :startHiring="handleGetStartedButtonClick" id="scoutCustomers" />
    <ScoutKeypoints :requestAccess="handleGetStartedButtonClick" />
    <ScoutWays :rawPlans="localizedCampaigns" :getStarted="handleGetStartedButtonClick" />
    <ScoutStartups />
    <sendHHMessageModal
      v-model="isSendHHMessageModalOpen"
      :job="emptyJob"
      :action="sendHHMessage"
    />
    <HHMessageIsSent v-model="isHHMessageIsSentModalOpen" :success-data="{}" :related="[]" />
  </div>
</template>

<script>
import Container from '@/components/Container';
import LandingQuotesGrid from '@/components/landing/LandingQuotesGrid';
import LandingBenefitCard from '@/components/landing/LandingBenefitCard';
import ScoutHero from './ScoutHero';
import ScoutStartups from './ScoutStartups';
import ScoutKeypoints from './ScoutKeypoints';
import ScoutCustomers from './ScoutCustomers';
import ScoutWays from './ScoutWays';
import sendHHMessageModal from '@/modals/headhunter/sendHHMessage.vue';
import HHMessageIsSent from '@/modals/headhunter/HHMessageIsSent.vue';
import { buildMetaHead } from '@/helpers/meta';
import LandingNumberedCard from '@/components/landing/LandingNumberedCard';
import { compileLocalizedPlans } from '@/components/monetisation/MonetisationUtils.js';

export default {
  name: 'Scout',
  components: {
    LandingQuotesGrid,
    Container,
    LandingNumberedCard,
    LandingBenefitCard,
    ScoutHero,
    ScoutStartups,
    ScoutKeypoints,
    ScoutCustomers,
    ScoutWays,
    sendHHMessageModal,
    HHMessageIsSent
  },
  head() {
    return buildMetaHead({
      title: 'Headhunting for Startups',
      description: `
        We have redesigned headhunting, for startups this time. Hire engineers,
        designers, marketers, business developers and PMs in 4 weeks or less.
      `,
      path: this.$route.path,
      imageUrl: process.env.BASE_URL + '/images/scout_meta.webp'
    });
  },
  data() {
    return {
      emptyJob: { criterias: [], questions: [], company: {} },
      localizedCampaigns: [],
      isSendHHMessageModalOpen: false,
      isHHMessageIsSentModalOpen: false,
      hero: {
        title: 'AI-powered Headhunting, designed for startups',
        subtitle: `
          Elevate your hiring game with a dedicated <b style='color:#4440DB'>Scout</b> equipped with access to The Hub’s extensive talent network of <b style='color:#4440DB'>650,000 startup-ready professionals</b>. We’ll have qualified & interested candidates booked on your calendar in no time. <b style='color:#4440DB'>Only pay if you hire</b>.
        `,
        button: {
          link: '/pricing',
          action: () => {
            this.handleGetStartedButtonClick();
          },
          secondaryAction: () => {
            // const section = document.getElementById('scoutCustomers');
            // if (section && section.scrollIntoView) {
            //   section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // }
          },
          label: 'Get started for free',
          newTab: true,
          external: true,
          backgroundColor: '#4440DB',
          color: 'white'
        },

        // caption: `or <div style='margin-left:0.3rem'><b style='text-decoration:underline; cursor:pointer'>book a call to learn more</b></div>`,
        caption: `or <a target="_blank" href="https://calendar.app.google/Si9262EWkGAUWzJN9" style='margin-left:0.3rem'><b style='text-decoration:underline; cursor:pointer; font-weight: 500'>book a call to learn more</b></a>`,
        image: require('~/assets/pages/scout/nicola.webp')
      }
    };
  },
  async mounted() {
    // chatShow({ user: this.user });
    await this.preInitialize({
      params: this.$route.params,
      axios: this.$axios
    });
    this.initialize();
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters['user/isLoggedIn'];
    },
    user() {
      return this.$store.getters['user/data'];
    },
    userCurrentProfile() {
      return this.$store.getters['user/currentProfile'];
    },
    products() {
      return this.$store.getters['meta/products'];
    },
    isLoggedIn() {
      return this.$store.getters['user/isLoggedIn'];
    },
    siteCountries() {
      return this.$store.getters['meta/siteCountries'];
    },
    sitePreferredCountryCode() {
      return this.$store.getters['meta/sitePreferredCountryCode'];
    }
  },
  methods: {
    sendHHMessage: async function(form) {
      try {
        // console.log('sendHHMessage form', form)
        const parsedFormData = {
          fullName: form.name,
          companyName: form.company,
          jobTitle: form.title,
          email: form.email,
          description: form.message
        };
        // console.log('sendHHMessage parsedFormData', form)
        await this.$axios.post(`/sendHHMessage`, parsedFormData);
        this.isSendHHMessageModalOpen = false;
        this.isHHMessageIsSentModalOpen = true;
      } catch (error) {
        this.isSendHHMessageModalOpen = false;
        alert('Something went wrong');
      }
    },
    preInitialize: async function({ axios }) {
      let { data: monetisingOptions } = await axios.get('/jobs/monetising-options');

      if (
        !this.isLoggedIn ||
        (this.userCurrentProfile && this.userCurrentProfile?.type !== 'company')
      ) {
        monetisingOptions = monetisingOptions.filter((option) => !option.isFree);
      }

      if (this.isLoggedIn && this.userCurrentProfile?.type === 'company') {
        if (
          this.userCurrentProfile.company &&
          this.userCurrentProfile.company.isFreeJobPostingAvailable
        ) {
          monetisingOptions = monetisingOptions.filter(
            (option) => option.name?.toUpperCase() !== 'BASIC'
          );
        } else {
          monetisingOptions = monetisingOptions.filter((option) => !option.isFree);
        }
      }

      this.monetisingOptions = monetisingOptions;
    },
    initialize: function() {
      try {
        this.company =
          this.userCurrentProfile?.type === 'company' ? this.userCurrentProfile.company : {};
        const companyCountryCode =
          this.company?.countries?.[0]?.countryCode || this.sitePreferredCountryCode || 'EU';

        const localizedCampaigns = compileLocalizedPlans({
          siteCountries: this.siteCountries,
          isJobRemote: true,
          companyCountryCode: companyCountryCode,
          isStandalone: this.isStandalone,
          allCampaigns: this.monetisingOptions,
          isLoggedIn: this.isLoggedIn,
          userCurrentProfile: this.userCurrentProfile,
          initCompany: this.company
        }).reduce((acc, item) => ({ ...acc, [item.title]: item }), {});

        this.localizedCampaigns = localizedCampaigns;

        this.isLoaded = true;
      } catch (err) {
        console.log('error', err);
      }
    },
    handleGetStartedButtonClick: function(planName = 'headhunter') {
      const plan = this.localizedCampaigns[planName.toUpperCase()];
      const step = planName.toLocaleLowerCase() === 'headhunter' ? 'scout_terms' : 'plan';
      const shouldRedirect =
        this.isLoggedIn && this.userCurrentProfile && this.userCurrentProfile.type === 'company';
      if (shouldRedirect) {
        const company = this.userCurrentProfile.key;
        this.$router.push(
          `/client/startups/${company}/jobs/new?plan=${
            plan.id
          }&step=${step}&shouldSkipPlanSelection=${planName === 'headhunter'}`
        );
      } else {
        this.isSendHHMessageModalOpen = true;
      }
    }
  }
};
</script>

<style lang="scss">
.scout a {
  cursor: pointer !important;
}

.headhunter-container-title {
  text-align: left;
  margin-bottom: 4.66rem;
  margin-top: -1rem;
  font-weight: 700;
}

.headhunter-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: blue;
}

.headhunter-hero-text_content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
}

.headhunter-hero-text_content-title {
  font-size: 3rem;
  color: black;
}

.headhunter-hero-text_content-subtitle {
  font-size: 2rem;
  color: grey;
}

.headhunter-hero-text_content-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
}

.headhunter-hero-text_content-button {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: magenta;
}
</style>
